<template>
    <v-container class="max-height">
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="2" sm="4">
                <company-select v-model="formData['company']" :items="companiesList"
                          density="compact" label="Select Company"
                          @update:model-value="fetchReport"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-select v-model="formData['assets']" :items="assetsList" chips clearable
                          density="compact" item-title="label" item-value="id" label="Select Asset(s)" multiple
                          @update:model-value="()=>{fetchAssetList();fetchReport()}"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-date-input
                    v-model="formData['start_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="Start Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['start_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-date-input
                    v-model="formData['end_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="End Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['end_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
        </v-row>

        <v-row v-if="formData['due']">
            <v-col cols="12">
                <v-chip closable color="warning" variant="tonal" prepend-icon="mdi-wrench-clock"
                        @click:close="clearDue">
                    Due now: scheduled inspections due and open maintenance, on enabled vehicles
                </v-chip>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="4" style="height:300px">
                <h2 class="text-start">Inspections Stats</h2>
                <Doughnut :data="{
                    labels: ['Pending', 'Skipped', 'Completed'],
                    datasets: [{
                        data: [stats['inspection']['pending'], stats['inspection']['skipped'], stats['inspection']['completed']],
                        backgroundColor: ['#d70000', '#ff9e01', '#849e00'],
                    }]}" :options="chartOptions"/>
            </v-col>
            <v-col cols="4" style="height:300px">
                <h2 class="text-start">Maintenance Stats</h2>
                <Doughnut :data="{
                    labels: ['Pending', 'Skipped', 'Completed'],
                    datasets: [{
                        data: [stats['maintenance']['pending'], stats['maintenance']['skipped'], stats['maintenance']['completed']],
                        backgroundColor: ['#d70000', '#ff9e01', '#849e00'],
                    }]}" :options="chartOptions"/>
            </v-col>
            <v-col cols="4" style="height:300px">
                <h2 class="text-start">Total Costs</h2>
                <Pie :data="{
                    labels: ['Inspection', 'Maintenance'],
                    datasets: [{
                        data: [stats['inspection']['cost'], stats['maintenance']['cost']],
                        backgroundColor: ['#2175F399', 'rgba(0,43,215,0.6)'],
                    }]}" :options="chartOptions"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <h2 class="mt-5 text-start">Orders List</h2>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Asset</th>
                        <th>Task</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Due</th>
                        <th>Cost</th>
                        <th><span class="d-sr-only">Details</span></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in reportData" v-if="reportData.length" class="text-left">
                        <td>
                            <router-link :to="taskTabLink(item)" target="_blank">{{ item.asset_name }}</router-link>
                        </td>
                        <td>{{ item.label || '-' }}</td>
                        <td class="text-capitalize">{{ item.type_name }}</td>
                        <td class="text-capitalize">{{ item.status_name }}</td>
                        <td class="text-no-wrap">{{ item.due_date ? String(item.due_date).slice(0, 10) : '-' }}</td>
                        <td>$ {{ item.total_cost }}</td>
                        <td class="text-right">
                            <v-btn :to="taskTabLink(item)" target="_blank" size="small" variant="text" color="primary"
                                   append-icon="mdi-chevron-right"
                                   :aria-label="`View ${item.type_name} details for ${item.asset_name}`">View</v-btn>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" class="text-center" colspan="7">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>


        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchReport"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {Pie, Doughnut} from "vue-chartjs";
import {onMounted, reactive, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js';
import {useAuthStore} from "@/stores/authStore";
import {useNavigation} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import CompanySelect from "@/components/globals/company-select.vue";

Chart.register(...registerables);

const {user} = useAuthStore();
const route = useRoute();
const router = useRouter();
const reportData = ref([]);
const totalPages = ref(1);
const loading = ref(false);
const currentPage = ref(1);
const assetsList = ref([]);
const companiesList = ref([]);
const {navigateTo} = useNavigation();
// Opens the vehicle on the matching tab (1 Maintenance Services, 0 Scheduled Inspections) with that task's details
const taskTabLink = (item) => ({
    name: 'asset',
    params: {asset: item.asset_id},
    query: {tab: item.type_name === 'maintenance' ? 1 : 0, ...(item.id ? {task: item.id} : {})},
});
const {showSnackbar} = useSnackbarStore();
const stats = ref({
    'maintenance': {
        'count': 0,
        'pending': 0,
        'skipped': 0,
        'completed': 0,
        'cost': 0.0,
    },
    'inspection':
        {
            'count': 0,
            'pending': 0,
            'skipped': 0,
            'completed': 0,
            'cost': 0.0,
        }
});
const formData = reactive({
    company: ref(user.type !== 'admin' ? user.company.id : null),
    start_date: null,
    end_date: null,
    assets: null,
    rows: 10,
    // ?due=1 from the menu count: only the orders that need the shop now.
    due: route.query.due === '1' ? 1 : null,
});

const clearDue = () => {
    formData['due'] = null;
    const query = {...route.query};
    delete query.due;
    router.replace({query});
    currentPage.value = 1;
    fetchReport();
};

// Opened again from the menu while already here: due orders only, no dates or vehicles kept.
watch(() => route.query.due, (due) => {
    if (route.name !== 'fleet/reports/inspection-maintenance') return;
    if ((due === '1') === !!formData['due']) return;
    formData['due'] = due === '1' ? 1 : null;
    if (formData['due']) {
        formData['start_date'] = null;
        formData['end_date'] = null;
        formData['assets'] = null;
    }
    currentPage.value = 1;
    fetchReport();
});

const chartOptions = {
    cutout: '45%', // default ~50%, increase for a thinner ring
    responsive: true,
    maintainAspectRatio: false, // allow resizing
    plugins: {
        legend: {
            position: 'right',
            labels: {
                boxWidth: 16,
                padding: 10
            }
        }
    }
};

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchReport();
        fetchAssetList();
    }
})

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companiesList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetList = () => {
    return send('get', `assets/list`, {company: formData['company']})
        .then((response) => {
            assetsList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Assets List could not be loaded', 'error')
        })
}
const fetchReport = () => {
    loading.value = true;
    send('get', `fleet/reports/inspection-maintenance-report`, {...formData, page: currentPage.value})
        .then((response) => {
            stats.value = response.data.data.stats;
            // The API sends an empty array, not a page, when the company has no assets
            const page = response.data.data.reportData;
            reportData.value = page?.data ?? [];
            totalPages.value = page?.last_page ?? 1;
            currentPage.value = page?.current_page ?? 1;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Report could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
</script>
